import { createContext, type ReactNode, useContext } from 'react';
import type { ArtifactLineageIndex, ArtifactRefMeta } from '~portal/stores';

const EMPTY: ArtifactLineageIndex = {
  latestRevisionByLineage: new Map(),
  labelByArtifactId: new Map(),
  metaByArtifactId: new Map(),
};

const ArtifactLineageContext = createContext<ArtifactLineageIndex>(EMPTY);

/**
 * @desc The conversation's artefact lineage, made reachable from inside the rendered prose.
 *
 * An inline `artifact:<id>` chip has to name the file it points at, and the transcript already
 * knows that name — `labelByArtifactId` is folded once per conversation from the tool-output
 * items (ADR-21). Threading it as a prop is not an option: the chip is created by
 * react-markdown's `components` map, several layers below the answer, from a markdown link the
 * model wrote. A context is the seam, and it carries the index that already exists rather than
 * minting a second one.
 *
 * Absent (the style preview, a test rendering MdContent bare) the chips fall back to the signed
 * URL's own name and then to the id, so nothing depends on the provider being there.
 */
export function ArtifactLineageProvider({
  lineage,
  children,
}: {
  lineage: ArtifactLineageIndex;
  children: ReactNode;
}) {
  return (
    <ArtifactLineageContext.Provider value={lineage}>{children}</ArtifactLineageContext.Provider>
  );
}

/**
 * @desc `<name>.<format>` for an artefact this conversation delivered, or undefined when the
 * transcript has no item for it — a reference to another conversation's artefact, or one stored
 * before names existed.
 */
export function useArtifactLabel(artifactId: string): string | undefined {
  return useContext(ArtifactLineageContext).labelByArtifactId.get(artifactId);
}

/**
 * @desc What the transcript says about one artefact — description, format, revision, size — or
 * undefined for a reference this conversation has no item for. The file card's only source of
 * everything the chip does not show (ADR-22).
 */
export function useArtifactMeta(artifactId: string): ArtifactRefMeta | undefined {
  return useContext(ArtifactLineageContext).metaByArtifactId.get(artifactId);
}

/** @desc `<name>.<format>` of the artefact a `derivedFrom` pointer names, when it resolves. */
export function useDerivedFromLabel(derivedFrom: string | null | undefined): string | undefined {
  const { labelByArtifactId } = useContext(ArtifactLineageContext);
  return derivedFrom ? labelByArtifactId.get(derivedFrom) : undefined;
}
